import type { TranslationKey } from '@/i18n/dictionary';
import { useTranslation } from '@/i18n/useTranslation';

import type { JourneyStep } from './stepper.steps';
import { JOURNEY_STEP } from './stepper.steps';

/**
 * The candidate's progress through the five-step journey — **a rail from `sm` up, a RING below it**
 * [Figma · Stepper `12084:26382`, and `Rail (mobile)` on every mobile frame that shows progress].
 *
 * Shared rather than per-feature because it appears on at least two screens owned by different stories —
 * `Tail · Pay card` at step 4 and `10 · Finish` at step 5 — and a progress rail that disagrees with itself
 * between two adjacent screens is worse than none. Every screen that shows it shows the same five steps in
 * the same order; only the position moves.
 *
 * **No story specifies it.** It appears in the frames and in no spec, which makes it a product statement
 * (there are five stages, and these are their names) that the Ground Truth does not yet record. Built to the
 * design, flagged for the Ground Truth.
 *
 * **At 375 the design does not draw a rail at all.** Every mobile frame with progress on it hides the
 * `Stepper` instance (`visible: false`) and draws `Rail (mobile)` instead: a 48px ring showing the current
 * step, filled clockwise from twelve o'clock like a circular loading bar and carrying «3 of 5» inside the
 * circle, with the step's BARE name beside it and the step that follows underneath.
 * Read live 2026-09-15 from the six frames that carry one — `New user / 06 · Jobs — Mobile` (12469:14778),
 * `08 · Fit questions` (14793), `09 · Submitted` (14808), `10 · Why a pay card` (14824), `10 · Pay card
 * linked` (14832) and `13 · That's it for now` (14857).
 *
 * That is not a scale of the rail, it is a different drawing. The frames' own fill fractions —
 * 0 · 25 · 50 · 75 · 100% for steps 1…5 — are the same `currentIndex / (steps − 1)` the bar already used,
 * so ONE number drives both presentations and they cannot drift apart.
 *
 * ⚠️ **The note this replaces had the reason backwards, and it is worth saying why.** It read «the frame is
 * illegible at 375» and paid 20px of wrapped height to make the five labels fit — «1 · Find a jo2 ·
 * Questions3 · Submitted…». What it had actually measured was the DESKTOP rail at a phone width. The design
 * does not put that rail there at all: it hides it and draws a ring. A frame that looks impossible at a
 * width is the signal to go and read that width's own frame, not evidence that the design is wrong; the
 * wrapped rail then sat in the build for as long as it did because the reasoning for it was written down
 * and read as settled.
 *
 * **One list, two presentations, on purpose.** The ring and the rail are the same `<ol>` with the same five
 * `<li>`, switched by CSS: below `sm` the four steps the candidate is not on are `sr-only`, so a screen
 * reader still hears five items and which one is current — the ring is decoration over that, never instead
 * of it. Two separate trees would have put two progress lists in the document and let them drift.
 */

/**
 * In order. The rail's whole meaning is the sequence, so it is declared once and never composed per screen.
 *
 * Two labels per step, because the two presentations say the position in different places: the rail numbers
 * it in the label («3 · Submitted») and the ring draws it inside the circle, where the frame then writes a
 * BARE «Submitted» beside it. One numbered label used in both would print the number twice at 375.
 */
const STEPS: readonly {
  readonly id: JourneyStep;
  readonly labelKey: TranslationKey;
  readonly bareLabelKey: TranslationKey;
}[] = [
  { id: JOURNEY_STEP.findJob, labelKey: 'stepper.findJob', bareLabelKey: 'stepper.findJob.bare' },
  {
    id: JOURNEY_STEP.questions,
    labelKey: 'stepper.questions',
    bareLabelKey: 'stepper.questions.bare',
  },
  {
    id: JOURNEY_STEP.submitted,
    labelKey: 'stepper.submitted',
    bareLabelKey: 'stepper.submitted.bare',
  },
  {
    id: JOURNEY_STEP.getSetUp,
    labelKey: 'stepper.getSetUp',
    bareLabelKey: 'stepper.getSetUp.bare',
  },
  { id: JOURNEY_STEP.allSet, labelKey: 'stepper.allSet', bareLabelKey: 'stepper.allSet.bare' },
];

export interface StepperProps {
  /** Where the candidate is. Everything before it is done; everything after is still ahead. */
  readonly current: JourneyStep;
}

export function Stepper({ current }: StepperProps) {
  const t = useTranslation();
  const currentIndex = STEPS.findIndex((step) => step.id === current);

  /**
   * The fill runs from the first sphere to the current one.
   *
   * The frame expresses it as an inset from the right — 0% on `Finish` (step 5) and 12% on the pay card
   * (step 4) — which is `(remaining steps / (steps - 1)) * (bar width)`. Computed rather than hard-coded per
   * screen so a sixth step, if one is ever added, cannot leave one screen's bar lying about the progress.
   */
  const fillPercent = currentIndex <= 0 ? 0 : (currentIndex / (STEPS.length - 1)) * 100;

  return (
    <ol
      aria-label={t('stepper.label')}
      className="relative flex w-full max-w-[500px] items-center sm:items-start"
    >
      {/*
       * The rail sits behind the spheres, inset 10% each side so it starts and ends at the first and last
       * sphere's centre rather than at the container's edge. `aria-hidden` because the fill duplicates what
       * each step's own state already announces — a screen reader reading a decorative bar twice is noise.
       */}
      <div
        aria-hidden
        className="absolute left-[10%] right-[10%] top-[10px] hidden h-1 overflow-hidden rounded-[var(--radius-xl)] bg-[var(--primary-muted)] sm:block"
      >
        <div className="h-full bg-[var(--primary)]" style={{ width: `${fillPercent}%` }} />
      </div>

      {STEPS.map((step, index) => {
        const done = index < currentIndex;
        const isCurrent = index === currentIndex;
        // Ahead of the candidate: an empty sphere in the muted tint, no check.
        const reached = done || isCurrent;
        // The step after this one, for the ring's «Next: …» line. Undefined on the last step, where the
        // frame draws no such line.
        const next = STEPS[index + 1];

        return (
          <li
            /*
             * Below `sm` only the CURRENT step is on screen — a row of ring and caption — and the other four
             * are `sr-only` rather than unrendered, so the list a screen reader hears is the same five
             * items at every width. `not-sr-only` restores the rail's own column from `sm` up.
             */
            className={
              isCurrent
                ? 'relative flex min-w-0 flex-1 items-center gap-3 sm:flex-col sm:gap-0'
                : 'sr-only sm:not-sr-only sm:relative sm:flex sm:min-w-0 sm:flex-1 sm:flex-col sm:items-center'
            }
            key={step.id}
            {...(isCurrent ? { 'aria-current': 'step' } : {})}
          >
            {isCurrent ? (
              <ProgressRing current={index + 1} percent={fillPercent} total={STEPS.length} />
            ) : null}
            <span
              className={`hidden size-6 items-center justify-center rounded-full sm:flex ${
                reached ? 'bg-[var(--primary)]' : 'bg-[var(--primary-muted)]'
              }`}
            >
              {/*
               * The CHECK is what says "done", not the fill colour — the state has to survive greyscale and
               * colour blindness. The current step and everything ahead of it carry no check, which is why
               * the pay-card frame's step 4 and the Finish frame's step 5 are bare spheres.
               */}
              {done ? <CheckGlyph /> : null}
            </span>
            {/*
              The ring's own caption block, below `sm` only [`Rail (mobile)` · `text`, read live 2026-09-15]:
              the step's BARE name — SF Pro Display Bold 15/18 in `--foreground` — over the step that follows
              it, 11/13 semibold in the muted tint, 2px apart.

              The number is not repeated here because the ring already draws it inside the circle. And there
              is no «Next:» line on the LAST step: `13 · That's it for now — Mobile` draws an 18-tall text
              block with «All set» alone, where every earlier frame draws 33.
            */}
            {isCurrent ? (
              <span className="flex min-w-0 flex-col items-start gap-0.5 sm:hidden">
                <span className="text-[15px] font-bold leading-[18px] text-[var(--foreground)]">
                  {t(step.bareLabelKey)}
                </span>
                {next ? (
                  <span className="text-[11px] font-semibold leading-[13px] text-[var(--muted-foreground)]">
                    {t('stepper.next', { label: t(next.bareLabelKey) })}
                  </span>
                ) : null}
              </span>
            ) : null}
            {/*
              The rail's own label, 14/20 in the muted tint, from `sm` up. On the CURRENT step it is hidden
              below `sm` — the caption block above says the same thing the way the mobile frame says it. On
              the other four it stays rendered at every width, because below `sm` their `<li>` is `sr-only`
              and `display: none` would take them out of the list a screen reader hears.

              It WRAPS inside its own column, centred and balanced, rather than holding one line [ISS-224].
              The frame gives each step a 100px column and every English label fits it (83–90 wide), so
              English still draws on one line. «1 · Buscar trabajo» does not: held to one line it ran 6px
              past each side of its column, into «2 · Preguntas», and the two read as one word. Wrapped, it
              takes two lines under its own dot. The words and the 14/20 type stay exactly as drawn — the
              copy is ISS's (0328), and only the line break is ours.

              The 4px inset each side keeps 8px between any two labels whatever their length. Without it,
              «Buscar trabajo» (95px) still fits a whole column, so the break fell after «1 ·» and left the
              number alone on the first line, 9.5px from its neighbour. With it a line holds 92px: the
              widest English label (88 in Inter, 90 in SF Pro) and «4 · Prepararte» (91) still fit.
            */}
            <span
              className={`min-w-0 text-sm leading-5 text-[var(--muted-foreground)] sm:text-balance sm:px-1 sm:text-center ${
                isCurrent ? 'hidden sm:block' : ''
              }`}
            >
              {t(step.labelKey)}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * The mobile progress ring [Figma · `Rail (mobile) / ring`, e.g. 12469:14809 on `09 · Submitted — Mobile`].
 *
 * Every value here is the frame's, read live 2026-09-15 rather than scaled off the rail: a 48 box, a 4px
 * band (`track` strokes the 48 ellipse INSIDE at 4; `progress` is an arc of `innerRadius` 0.833 — 0.833 ×
 * 24 = 20, so the same 4), `--border-subtle` #d7d6d5 under `--primary` #00808e. **Not the bar's
 * `--primary-muted`**, which is the tint the horizontal rail uses and 20 shades lighter than what is drawn
 * here.
 *
 * It fills clockwise FROM TWELVE O'CLOCK, which is what `startingAngle: -π/2` says in every frame and why
 * the circle is rotated a quarter turn: a dash offset alone starts a stroke at three o'clock, a quarter
 * turn out from the design on all six screens.
 *
 * `aria-hidden`, like the rail's bar: the list around it already announces the five steps and which one is
 * current, and a decorative arc read out twice is noise.
 */
interface ProgressRingProps {
  /** How much of the journey is behind the candidate, 0–100 — the same number the rail's bar uses. */
  readonly percent: number;
  /** The step the candidate is on, 1-based, as the ring writes it: «3 of 5». */
  readonly current: number;
  /** How many steps there are. Passed rather than read here so one list stays the only source of the count. */
  readonly total: number;
}

/** 48 across with a 4px band: the stroke is centred on its path, so the radius is 24 − 4/2. */
const RING_RADIUS = 22;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

function ProgressRing({ current, percent, total }: ProgressRingProps) {
  const t = useTranslation();

  return (
    <span aria-hidden className="relative block size-12 shrink-0 sm:hidden">
      <svg className="size-12" viewBox="0 0 48 48">
        <circle
          cx="24"
          cy="24"
          fill="none"
          r={RING_RADIUS}
          stroke="var(--border-subtle)"
          strokeWidth="4"
        />
        <circle
          cx="24"
          cy="24"
          fill="none"
          r={RING_RADIUS}
          stroke="var(--primary)"
          strokeDasharray={RING_LENGTH}
          strokeDashoffset={RING_LENGTH * (1 - percent / 100)}
          strokeWidth="4"
          transform="rotate(-90 24 24)"
        />
      </svg>
      {/*
        The count, INSIDE the circle — SF Pro Display Bold 10/12 in `--foreground`, and the frame draws it
        as a text box the full 48x48, centred on both axes, not a line sitting on the ring's midpoint.

        `aria-hidden` with the arc around it: the list already announces five items and which one is
        current, so a screen reader reading «3 of 5» here would hear the position twice.
      */}
      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold leading-3 text-[var(--foreground)]">
        {t('stepper.ringProgress', { current, total })}
      </span>
    </span>
  );
}

/**
 * 16px inside a 24px sphere.
 *
 * Sized to the GLYPH, never to the sphere. Sizing an inset glyph to its containing box is how a chevron on
 * this project came out first 2× and then 4× too large, twice in the same review.
 */
function CheckGlyph() {
  return (
    <svg
      aria-hidden
      className="size-4"
      fill="none"
      stroke="var(--primary-foreground)"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      viewBox="0 0 16 16"
    >
      <path d="M3.5 8.5 6.5 11.5 12.5 5" />
    </svg>
  );
}
